import React from "react";
import { toaster, Message as RsuitMessage } from "rsuite";

import { useTranslation } from "react-i18next";

import constants from "utils/constants";

import {
    FailureAction,
    IWorkflowStep,
    IWorkflowWithData,
    StudioWorkflow,
    StudioWorkflowType,
    WorkflowStatus,
} from "store/workflows/workflows-types";

import {
    resolveStudioWorkflow,
    terminateStudioWorkflow,
} from "api/environments/tenants/studio-api";

import { getErrorMessage } from "api/defaults";

import Modal from "components/Workflows/Progress/Modal";
import StepList from "components/Workflows/Progress/Step/List";
import Header from "components/Workflows/Progress/Header/Header";
import Username from "components/Workflows/Progress/Username/Username";
import Message from "components/Workflows/Progress/Message/Message";
import Footer from "components/Workflows/Progress/Footer/Footer";

interface Props {
    open: boolean;

    environmentId: string;
    tenantId: string;
    workflow: IWorkflowWithData<IWorkflowStep, any> | null;

    onClose: () => void;
    onTerminated?: () => void;
    onResolved?: () => void;
}

const Progress: React.FC<Props> = ({
    open,
    environmentId,
    tenantId,
    workflow,
    onClose,
    onTerminated,
    onResolved,
}: Props) => {
    const { t } = useTranslation();

    const pushError = (payload: unknown, status: number) => {
        toaster.push(
            <RsuitMessage showIcon type="error" closable>
                {getErrorMessage(String(payload), status)}
            </RsuitMessage>,
            { duration: constants.alertDurationMsec }
        );
    };

    const studioWorkflow = workflow as StudioWorkflow;

    if (studioWorkflow === null) {
        return null;
    }

    const getTitle = () => {
        switch (studioWorkflow.type) {
            case StudioWorkflowType.RotateClientSecret:
                return t("tenantConfig.studio.workflows.rotateClientSecret");
            case StudioWorkflowType.Integration:
            default:
                return t("tenantConfig.studio.workflows.integration");
        }
    };

    const getStepsStyle = () => {
        switch (studioWorkflow.status) {
            case WorkflowStatus.Terminated:
                return "workflow-modal__steps--terminated";
            default:
                return "";
        }
    };

    const sepsStyle = getStepsStyle();
    const title = getTitle();

    const handleResolve = (action: FailureAction): Promise<void> =>
        resolveStudioWorkflow(
            environmentId,
            tenantId,
            studioWorkflow.id,
            action
        ).then(([payload, status]) => {
            if (status === constants.statusCode.OK) {
                onResolved?.();
            } else {
                pushError(payload, status);
            }
        });

    const handleTerminate = (): Promise<void> =>
        terminateStudioWorkflow(
            environmentId,
            tenantId,
            studioWorkflow.id
        ).then(([payload, status]) => {
            if (status === constants.statusCode.OK) {
                onTerminated?.();
            } else {
                pushError(payload, status);
            }
        });

    return (
        <Modal
            open={open}
            header={<Header workflow={studioWorkflow} title={title} />}
            steps={
                <StepList steps={studioWorkflow.steps} className={sepsStyle} />
            }
            userMessage={<Username workflow={studioWorkflow} />}
            completeMessage={<Message workflow={studioWorkflow} />}
            footer={
                <Footer
                    workflow={studioWorkflow}
                    onResolve={handleResolve}
                    onTerminate={handleTerminate}
                    onClose={onClose}
                />
            }
            onClose={onClose}
        />
    );
};

export default Progress;
